import type { ReactNode } from 'react';
import { CheckIcon, ChevronRightIcon } from './icons';

export type ChecklistTone = 'done' | 'todo' | 'pending' | 'error' | 'optional';

/**
 * Status dot of a checklist row (screen 06): filled `success` with a check when done, an
 * `accent` ring when to do, `line-strong` ring when optional; `pending` (sent, waiting for
 * review) a dashed `ink-muted` ring; `error` a `danger` ring.
 */
export function StatusDot({ tone }: { tone: ChecklistTone }) {
  if (tone === 'done') {
    return (
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-success text-paper">
        <CheckIcon size={14} />
      </span>
    );
  }
  const ring = {
    todo: 'border-accent',
    pending: 'border-dashed border-ink-muted',
    error: 'border-danger',
    optional: 'border-line-strong',
  }[tone];
  return <span className={`size-6 shrink-0 rounded-full border-2 ${ring}`} />;
}

/**
 * One row of "À compléter avant la course" (screen 06): dot, title, hint, chevron. A disclosure
 * button: the row's fields open below it (`children`) when `open`.
 */
export function ChecklistRow({
  id,
  tone,
  title,
  hint,
  hintTone = 'muted',
  stateLabel,
  open,
  onToggle,
  children,
}: {
  id: string;
  tone: ChecklistTone;
  title: ReactNode;
  hint: ReactNode;
  hintTone?: 'muted' | 'accent' | 'danger';
  /** Screen-reader text for the dot. */
  stateLabel: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  const hintClass = { muted: 'text-ink-muted', accent: 'text-accent', danger: 'text-danger' }[
    hintTone
  ];
  return (
    <div id={id} className="border-t border-line">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        onClick={onToggle}
        className="flex w-full items-center gap-3 py-3 text-left text-ink"
      >
        <StatusDot tone={tone} />
        <span className="sr-only">{stateLabel}</span>
        <span className="flex flex-1 flex-col">
          <span className="font-semibold">{title}</span>
          <span className={`text-helper ${hintClass}`}>{hint}</span>
        </span>
        <ChevronRightIcon
          size={18}
          className={`shrink-0 text-ink-subtle transition-transform ${open ? 'rotate-90' : ''}`}
        />
      </button>
      {open && (
        <div id={`${id}-panel`} className="flex flex-col gap-3.5 pb-4">
          {children}
        </div>
      )}
    </div>
  );
}
